import { useAppContextController } from "context/AppContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

import { Card, Checkbox, Grid, Icon, Stack, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import DataTable from "components/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";

import fetchCustomers from "api/companies/fetchCustomers";
import CompanyLogoSquare from "components/CompanyLogoSquare";
import fetchVenues from "layouts/pages/venues/actions/fetchVenues";

import FiltersList from "components/FiltersList";
import Searchbar from "components/Searchbar";
import VenueIcon from "components/VenueIcon";
import useSort from "utils/useSort";
// import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Mail } from "@mui/icons-material";
import fetchSingleCustomer from "api/companies/fetchSingleCustomer";
import ConfirmationModal from "components/ConfirmationModal";
import DatePeriodSelector from "components/DatePeriodSelector";
import MDButton from "components/MDButton";
import useHandleBackButtonSubGrid from "hooks/useHandleBackButtonSubGrid";
import usePreferredPageSize from "hooks/usePreferredPageSize";
import useSessionAuth from "hooks/useSessionAuth";
import deleteInvoiceBatch from "layouts/pages/payroll/actions/deleteInvoiceBatch";
import fetchEventsJobs from "layouts/pages/payroll/actions/fetchEventsJobs";
import fetchInvoices from "layouts/pages/payroll/actions/fetchInvoices";
import fetchSingleInvoice from "layouts/pages/payroll/actions/fetchSingleInvoice";
import updateInvoiceBatch from "layouts/pages/payroll/actions/updateInvoiceBatch";
import InvoiceEditorPreviewModal from "layouts/pages/payroll/components/InvoiceEditorTable/components/InvoiceEditorPreviewModal";
import SendInvoiceMessageModal from "layouts/pages/payroll/components/SendInvoiceMessageModal";
import fetchSingleVenueFromSlug from "layouts/pages/venues/actions/fetchSingleVenueFromSlug";
import { cloneDeep } from "lodash";
import moment from "moment";
import { useSnackbar } from "notistack";
import { calculateInvoiceSummary } from "utils/helpers/invoiceHelper";
import { getPeriodForDate } from "utils/helpers/payrollHelper";
import InvoiceTableActions from "../InvoicesTableActions";
import SendMultipleInvoicesModal from "../SendMultipleInvoicesModal";

const useStyle = makeStyles({
  box: {
    marginTop: 20,
    overflow: "visible!important",
  },
  addButton: {
    fontSize: 40,
  },
});

const InvoicesTable = ({
  // eventUrl,
  navigateToAction,
  title = "Invoices",
  filters,
  setFilters,
  fetchAll,
  setActionSelected,
  defaultSort,
  isMobile,
  setToastAlert,
  name,
  parentPage,
  parentNavigateWithBack,
  parentNavigateToScreenWithBack,
  bindNavigateFunc,
  currentPeriod,
  setCurrentPeriod,
}) => {
  const [isMobileWidth, setIsMobileWidth] = useState(false);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState(null);
  const [selectedInvoiceForDeletion, setSelectedInvoiceForDeletion] = useState(null);
  const [selectedInvoiceForVoiding, setSelectedInvoiceForVoiding] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [selectedInvoiceIdsForSend, setSelectedInvoiceIdsForSend] = useState(null);
  const [invoiceFiles, setInvoiceFiles] = useState(null);
  const {
    venues,
    setVenues,
    currentLoggedUser,
    currentApplicant,
    setCurrentApplicant,
    userType,
    company,
    setCompany,
    setCompanyType,
    currentCompany,
    setCurrentCompany,
    currentInvoice,
    setCurrentInvoice,
  } = useAppContextController();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsMobileWidth(true);
      } else {
        setIsMobileWidth(false);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const classes = useStyle();

  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(1);
  const { limit, setLimit, pageLimitConfirmationModal } = usePreferredPageSize(5);
  const { order, orderBy, toggleSort } = useSort();
  const { logoutAuth0User } = useSessionAuth();
  const isCompany = company?.companyType === "Company";

  // Handle back button from parent screens
  const { navigateWithBack, navigateWithoutBack } = useHandleBackButtonSubGrid({
    parentPage,
    setPage,
    page,
    filters,
    navigateFunc: navigate,
    navigateWithBackFunc: parentNavigateWithBack,
  });

  const deleteDataMutation = useMutation(deleteInvoiceBatch);
  const updateDataMutation = useMutation(updateInvoiceBatch);

  // Handle back button to a different screen
  const navigateToScreenWithBack = useMemo(
    () =>
      parentNavigateToScreenWithBack
        ? (url) => parentNavigateToScreenWithBack(url, page, filters)
        : (url) => navigate(url),
    [filters, navigate, page, parentNavigateToScreenWithBack]
  );

  useEffect(() => {
    bindNavigateFunc?.(navigateWithBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateWithBack]);

  const startOfPeriodDay = company?.payrollSettings?.payrollStartOfPeriod ?? 1;
  const periodLength = company?.payrollSettings?.payrollPeriodLength || "weekly";
  const shiftPeriodWeek = (company?.payrollSettings?.payrollShiftPeriodWeek || "No") === "Yes";

  const [calendarMiddleDate, setCalendarMiddleDate] = useState(null);

  const handleSearchEventJob = (entity) => {
    setCurrentInvoice(null);

    const params = [];
    if (filters.entityType) params.push(`entityType=${filters.entityType}`);
    if (entity.eventUrl) params.push(`eventUrl=${entity.eventUrl}`);
    if (entity.jobSlug) params.push(`jobSlug=${entity.jobSlug}`);
    navigate(`/invoices${params.length ? `?${params.join("&")}` : ""}`);
  };

  const handleSearch = (entity) => {
    setCurrentInvoice(null);

    const params = [];
    if (filters.entityType) params.push(`entityType=${filters.entityType}`);
    if (entity.slug) {
      if (company?.companyType === "Venue") params.push(`venueSlug=${entity.slug}`);
      else params.push(`companySlug=${entity.slug}`);
    }
    navigate(`/invoices${params.length ? `?${params.join("&")}` : ""}`);
  };

  const stringifiedFilters = JSON.stringify(filters ?? {});

  // reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [stringifiedFilters]);

  const options = useMemo(
    () =>
      fetchAll
        ? { fetchAll }
        : {
            page,
            limit,
            order,
            orderBy,
          },
    [fetchAll, limit, order, orderBy, page]
  );

  const allOptions = useMemo(() => ({ ...options, filters, venues }), [filters, options, venues]);

  const invoiceFetchFunc = () => {
    const allOptionsForQuery = cloneDeep(allOptions);

    allOptionsForQuery.startDate = currentPeriod?.[0]?.toISOString();
    allOptionsForQuery.endDate = currentPeriod?.[1]?.toISOString();
    return fetchInvoices(allOptionsForQuery);
  };

  const {
    data: invoiceData,
    isLoading,
    refetch: refetchInvoiceData,
  } = useQuery(["invoices", allOptions, currentPeriod], invoiceFetchFunc, {});

  // Venue or Customer data for sending email to contacts
  const {
    data: previewInvoiceVenueCustomer,
    isLoading: isLoadingVenueCustomer,
    refetch: refetchVenueCustomer,
  } = useQuery(
    [
      "venueCustomer",
      {
        companySlug: selectedInvoiceForPreview?.companySlug,
        venueSlug: selectedInvoiceForPreview?.venueSlug,
      },
    ],
    () =>
      company?.companyType === "Company"
        ? fetchSingleCustomer({
            slug: selectedInvoiceForPreview?.companySlug,
          })
        : fetchSingleVenueFromSlug({
            slug: selectedInvoiceForPreview?.venueSlug,
          }),
    {
      enabled: !!selectedInvoiceForPreview?.jobSlug || !!selectedInvoiceForPreview?.eventUrl,
    }
  );

  const filteredData = useMemo(() => {
    const data = invoiceData?.data ? invoiceData : { data: [], count: 0 };
    return data;
  }, [invoiceData]);

  const allChecked = useMemo(() => {
    if (!filteredData?.data?.length) return false;
    for (let index = 0; index < filteredData.data.length; index += 1) {
      const element = filteredData?.data[index];
      if (!selectedRows[element?._id]) return false;
    }
    return true;
  }, [filteredData.data, selectedRows]);

  const numChecked = useMemo(() => {
    return Object.values(selectedRows ?? {}).length;
  }, [selectedRows]);

  const [allCheckedSameClient, multipleSelectionVenueCustomerSlug] = useMemo(() => {
    const selectedArray = Object.values(selectedRows ?? {});
    if (!selectedArray.length) return [false, null];
    let firstVenueCustomer = null;
    for (let index = 0; index < selectedArray.length; index += 1) {
      const element = selectedArray[index];
      if (element) {
        if (!firstVenueCustomer) firstVenueCustomer = element;
        else if (firstVenueCustomer !== element) return [false, null];
      }
    }
    return [true, firstVenueCustomer];
  }, [selectedRows]);

  // Venue or Customer data for sending multiple email to contacts
  const {
    data: sendMultipleInvoicesVenueCustomer,
    isLoading: isLoadingSendMultipleInvoicesVenueCustomer,
    refetch: refetchSendMultipleInvoicesVenueCustomer,
  } = useQuery(
    [
      "sendMultipleInvoicesVenueCustomer",
      {
        slug: multipleSelectionVenueCustomerSlug,
      },
    ],
    () =>
      company?.companyType === "Company"
        ? fetchSingleCustomer({
            slug: multipleSelectionVenueCustomerSlug,
          })
        : fetchSingleVenueFromSlug({
            slug: multipleSelectionVenueCustomerSlug,
          }),
    {
      enabled: !!multipleSelectionVenueCustomerSlug && !!selectedInvoiceIdsForSend?.length,
    }
  );

  // Updated invoice information for preview
  const {
    data: previewInvoiceData,
    isLoading: isLoadingPreviewInvoiceData,
    refetch: refetchPreviewInvoiceData,
  } = useQuery(
    ["invoiceData", selectedInvoiceForPreview?._id],
    () => fetchSingleInvoice({ invoiceId: selectedInvoiceForPreview?._id }),
    {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!selectedInvoiceForPreview?._id,
    }
  );

  const { action, invoiceId } = useParams();

  const getAndSetFullInvoice = useCallback(
    async (invId) => {
      try {
        const inv = await fetchSingleInvoice({ invoiceId: invId });
        if (inv) setCurrentInvoice(inv);
      } catch (error) {
        if (String(error).includes("401") || error?.response?.status === 401) {
          logoutAuth0User();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getAndSetFullInvoiceForPreview = useCallback(
    async (invId) => {
      try {
        const inv = await fetchSingleInvoice({ invoiceId: invId });
        if (inv) setSelectedInvoiceForPreview(inv);
      } catch (error) {
        if (String(error).includes("401") || error?.response?.status === 401) {
          logoutAuth0User();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleDelete = useCallback(
    async (row) => {
      const result = await deleteDataMutation.mutateAsync({
        invoiceId: row?._id,
      });
      if (result?.data?.success) {
        enqueueSnackbar("Invoice deleted successfully", { variant: "success" });
        refetchInvoiceData();
        setCurrentInvoice(null);
      } else {
        enqueueSnackbar("An error has occurred", { variant: "success" });
      }
    },
    [deleteDataMutation, enqueueSnackbar, refetchInvoiceData, setCurrentInvoice]
  );

  const handleVoid = useCallback(
    async (row) => {
      const result = await updateDataMutation.mutateAsync({
        invoiceId: row?._id,
        data: {
          status: "Void",
        },
      });
      if (result?.data?.success) {
        enqueueSnackbar("Invoice voided successfully", { variant: "success" });
        refetchInvoiceData();
        setCurrentInvoice(null);
      } else {
        enqueueSnackbar("An error has occurred", { variant: "success" });
      }
    },
    [enqueueSnackbar, refetchInvoiceData, setCurrentInvoice, updateDataMutation]
  );

  useEffect(() => {
    if (!isLoading && invoiceId) {
      getAndSetFullInvoice(invoiceId);
    }
  }, [isLoading, invoiceId, getAndSetFullInvoice]);

  useEffect(() => {
    if ((filters?.venueSlug || filters?.companySlug) && action) setPage(1);
    // causes interviews to misbehave, uncomment if needed
    // if (!filters?.timeFrame && !filters?.eventUrl && setFilters)
    //   setFilters((prev) => ({
    //     ...prev,
    //     eventType: prev.eventType || "Event",
    //   }));
  }, [filters?.venueSlug, action, filters?.companySlug]);

  function showSmallDate(date) {
    const opts = {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "numeric",
      seconds: "omit",
      hourCycle: "h12", // or 'h23' for 24-hour format
    };
    return date.toLocaleString("en-US", opts).replace(",", ""); // remove the comma separator
  }

  const handleCheckAll = useCallback(
    (e) => {
      if (!filteredData?.data?.length) return;
      const newSelected = { ...selectedRows };
      for (let index = 0; index < filteredData.data.length; index += 1) {
        const element = filteredData?.data[index];
        newSelected[element?._id] = e.target.checked
          ? element?.venueSlug || element?.companySlug
          : null;
      }
      setSelectedRows(newSelected);
    },
    [filteredData.data, selectedRows]
  );

  const handleSendMultipleInvoices = useCallback(
    (e) => {
      const selectedArray = Object.entries(selectedRows ?? {}).filter(([key, value]) => !!value);
      if (!selectedArray.length) return;

      setSelectedInvoiceIdsForSend(selectedArray.map(([key, value]) => key));
    },
    [selectedRows]
  );

  const columns = useMemo(
    () => [
      {
        title: "",
        field: "checked",
        customHeaderCell: () => (
          <Checkbox
            sx={{ paddingTop: 0, paddingBottom: 0 }}
            checked={allChecked}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={handleCheckAll}
          />
        ),
        customCell: (checked, field, row) => (
          <Checkbox
            checked={!!selectedRows[row?._id]}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              setSelectedRows((curr) => ({
                ...curr,
                [row?._id]: e.target.checked ? row?.venueSlug || row?.companySlug : null,
              }));
            }}
            inputProps={{ "aria-label": "controlled" }}
          />
        ),
      },
      ...(company?.companyType === "Venue"
        ? [
            {
              title: "Logo",
              field: "logoUrl",
              customCell: (logoUrl, field, row) => (
                <VenueIcon
                  logoUrl={logoUrl || venues[row?.venueSlug]?.logoUrl}
                  slug={row?.venueSlug}
                  name={row?.venueName}
                  // goToEnabled={userType !== "User"}
                  goToEnabled
                />
              ),
            },
            {
              title: "Venue",
              field: "venueSlug",
              customCell: (slug) => <span>{slug?.toUpperCase()}</span>,
            },
          ]
        : [
            {
              title: "Logo",
              field: "name",
              customCell: (_name, field, row) => (
                <CompanyLogoSquare company={row} defaultCompany={company} />
              ),
            },
            {
              title: "Customer",
              field: "companySlug",
              customCell: (slug) => <span>{slug?.toUpperCase()}</span>,
            },
          ]),
      {
        title: "Event/Job",
        field: "eventName",
        customCell: (eventName, field, row) => <span>{row?.eventName || row?.title}</span>,
      },
      {
        title: "Type",
        field: "jobSlug",
        customCell: (eventName, field, row) => <span>{row?.jobSlug ? "Shift Job" : "Event"}</span>,
      },
      {
        title: "Date Created",
        field: "createdDate",
        customCell: (date) => (date ? moment(date).format("YYYY-MM-DD") : ""),
      },
      {
        title: "Status",
        field: "status",
      },
      {
        title: "Invoice #",
        field: "invoiceNumber",
        customCell: (num) => (num ? num.toString().padStart(8, "0") : ""),
      },
      {
        title: "Amount",
        field: "totalAmount",
        customCell: (totalAmount) =>
          `${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
            totalAmount || 0
          )}`,
      },
      {
        title: "Invoice Actions",
        field: "_id",
        customCell: (url, field, row) => (
          <InvoiceTableActions
            setNavigateUrl={navigateWithBack}
            setNavigateUrlToScreen={navigateToScreenWithBack}
            eventUrl={url}
            row={row}
            setFilters={setFilters}
            setActionSelected={setActionSelected}
            // setOpen={setOpen}
            // setEventPreview={setEventPreview}
            // setJobPreview={setJobPreview}
            // getAndSetFullEvent={getAndSetFullEvent}
            // getAndSetFullJob={getAndSetFullJob}
            getAndSetFullInvoice={getAndSetFullInvoice}
            setInvoiceForPreview={setSelectedInvoiceForPreview}
            setInvoiceForDeletion={setSelectedInvoiceForDeletion}
            setInvoiceForVoiding={setSelectedInvoiceForVoiding}
            // openTimeModal={() => {
            //   setCurrentEvent(row);
            //   toggleTimeModal(true);
            // }}
          />
        ),
      },
    ],
    [
      company,
      allChecked,
      handleCheckAll,
      selectedRows,
      venues,
      navigateWithBack,
      navigateToScreenWithBack,
      setFilters,
      setActionSelected,
      getAndSetFullInvoice,
    ]
  );

  const customerColumns = useMemo(
    () => [
      {
        title: "Logo",
        field: "name",
        customCell: (_name, field, row) => (
          <CompanyLogoSquare company={row} defaultCompany={company} />
        ),
      },
      {
        title: "Customer ID",
        field: "slug",
        customCell: (slug) => <div>{slug?.toUpperCase()}</div>,
      },
      { title: "Customer Name", field: "name" },
      { title: "City", field: "city" },
      { title: "State", field: "state" },
    ],
    [company]
  );

  const venueColumns = useMemo(
    () => [
      {
        title: "Logo",
        field: "logoUrl",
        customCell: (logoUrl, field, row) => (
          <VenueIcon logoUrl={logoUrl} slug={row?.slug} name={row?.name} />
        ),
      },
      {
        title: "Venue",
        field: "slug",
        customCell: (slug) => <span>{slug?.toUpperCase()}</span>,
      },
      {
        title: "Venue Name",
        field: "name",
      },
      {
        title: "City",
        field: "city",
      },
      {
        title: "State",
        field: "state",
      },
      { title: "Status", field: "status" },
    ],
    []
  );

  const onRowClick = (row, e) => {
    if (!["Master", "Admin"].includes(userType)) return null;
    e.stopPropagation();
    // setCurrentEvent(row);
    getAndSetFullInvoice(row._id);

    const basePath = new URL(window.location).pathname;
    const nodes = basePath.split("/");
    const navigateFunc = navigateWithoutBack;
    navigateFunc(`/invoices/${row?._id}${navigateToAction ? `/action/${navigateToAction}` : ""}`);
    return true;
  };

  const handleChangePeriod = (v) => {
    setCurrentPeriod(
      getPeriodForDate(v.toISOString(), periodLength, shiftPeriodWeek, startOfPeriodDay)
    );

    const params = [];
    if (filters.entityType) params.push(`entityType=${filters.entityType}`);
    if (filters.venueSlug) params.push(`venueSlug=${filters.venueSlug}`);
    if (filters.companySlug) params.push(`companySlug=${filters.companySlug}`);
    if (filters.eventUrl || filters.jobSlug) {
      const newFilt = { ...filters };
      delete newFilt.eventUrl;
      delete newFilt.jobSlug;
      setFilters(newFilt);
    }
    navigateWithoutBack(`/invoices${params.length ? `?${params.join("&")}` : ""}`);
  };

  const calculatedSummary = useMemo(() => {
    if (!previewInvoiceData) return [];
    return calculateInvoiceSummary({
      details: previewInvoiceData?.details ?? [],
      isShiftJob: !!previewInvoiceData?.jobSlug,
      eventName: previewInvoiceData?.eventName,
      jobName: previewInvoiceData?.jobName,
      date: previewInvoiceData?.jobSlug
        ? ""
        : moment(previewInvoiceData?.eventDate)
            .tz(previewInvoiceData?.timeZone || "America/Chicago")
            .format("MM/DD/YYYY"),
    });
  }, [previewInvoiceData]);

  return (
    <Card className={classes.box}>
      <Grid container spacing={3} alignItems="center" justifyContent="space-between" py={1} pr={2}>
        <Grid item xs={12} sTablet={5}>
          <MDBox display="flex" flexWrap="wrap">
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="4rem"
              height="4rem"
              variant="gradient"
              bgColor="violet"
              color="white"
              borderRadius="xl"
              ml={3}
              mt={-6}
            >
              <Icon fontSize="large">account_balance</Icon>
            </MDBox>
            <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
              {title} - List View{" "}
              {invoiceData?.data?.length
                ? `- ${invoiceData.data.length} Invoice${invoiceData.data?.length > 1 ? "s" : ""}`
                : ""}
            </MDTypography>
            <MDBox ml={3}>
              <Grid item xs={12}>
                <FiltersList filters={filters} setFilters={setFilters} />
              </Grid>
            </MDBox>
          </MDBox>
        </Grid>
        <Grid item xs={12} sTablet={7} alignItems="center">
          <Grid container direction="row" alignItems="flex-end" justifyContent="space-between">
            <Grid item sm={11.5}>
              <Grid container spacing={2}>
                <Grid item xl={6} minWidth="500px" alignItems="center">
                  <Stack direction="row" alignItems="center">
                    <MDTypography variant="h5" color="dark" sx={{ ml: 2 }}>
                      Period:
                    </MDTypography>
                    <DatePeriodSelector value={currentPeriod} onChange={handleChangePeriod} />
                  </Stack>
                </Grid>
                <Grid container item minWidth="500px" xs={12} xl={6}>
                  <Grid item xs={6}>
                    <Searchbar
                      fetch={(params) =>
                        fetchEventsJobs({
                          ...params,
                          startDate: currentPeriod?.[0]?.toISOString(),
                          endDate: currentPeriod?.[1]?.toISOString(),
                        })
                      }
                      fetchAll={false}
                      placeholder={
                        company?.companyType === "Venue" ? "Search Event/Job" : "Search Job"
                      }
                      columns={columns.slice(1, columns.length - 1)}
                      queryCharacterLimit={4}
                      resultsLimit={25}
                      setPage={setPage}
                      searchBy={["eventUrl", "eventName", "title", "jobSlug"]}
                      noFilter
                      idField="_id"
                      scrollLimit={200}
                      onRowClick={handleSearchEventJob}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    {company?.companyType === "Venue" ? (
                      <Searchbar
                        fetch={fetchVenues}
                        fetchAll={false}
                        placeholder="Search Venue/City"
                        columns={venueColumns}
                        queryCharacterLimit={2}
                        resultsLimit={10}
                        setFilters={setFilters}
                        setPage={setPage}
                        searchBy={["slug", "name", "city"]}
                        filterBy="venueSlug"
                        filterByColumn="slug"
                        idField="slug"
                        onRowClick={handleSearch}
                        // returnAfterClick={userType === "User"}
                        returnAfterClick={false}
                        // filterData={
                        //   userType === "User"
                        //     ? (item) => {
                        //         return item.status === "Active" && !lockedVenues.includes(item.slug);
                        //       }
                        //     :
                        //     undefined
                        // }
                      />
                    ) : (
                      <Searchbar
                        fetch={fetchCustomers}
                        fetchAll={false}
                        placeholder="Search Customers"
                        columns={customerColumns}
                        queryCharacterLimit={2}
                        resultsLimit={10}
                        setFilters={setFilters}
                        setPage={setPage}
                        searchBy={["slug", "name", "city"]}
                        filterBy="companySlug"
                        filterByColumn="slug"
                        idField="slug"
                        onRowClick={handleSearch}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MDBox pt={2} pb={2}>
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={(row, e) => {
            onRowClick(row, e);
          }}
          page={page}
          limit={limit}
          setPage={setPage}
          setLimit={setLimit}
          order={order}
          orderBy={orderBy}
          toggleSort={toggleSort}
          defaultSort={defaultSort}
          fetchAll={fetchAll}
          isLoading={isLoading} // Extract isLoading from useQuery then pass here (required)
          greybar
          divider
          idField="_id"
          leftFooter={
            <Grid
              container
              sx={{
                alignSelf: "flex-start",
                alignItems: "center",
                width: "auto",
              }}
            >
              <MDTypography variant="h5" color="dark" sx={{ ml: 2, mr: 2 }}>
                {`Selected: ${numChecked}`}
              </MDTypography>
              <Tooltip
                title={
                  !!numChecked && !allCheckedSameClient
                    ? `All selected invoices must be from the same ${
                        isCompany ? "customer" : "venue"
                      }.`
                    : ""
                }
              >
                <span>
                  <MDButton
                    onClick={handleSendMultipleInvoices}
                    startIcon={<Mail />}
                    color="info"
                    variant="contained"
                    disabled={!allCheckedSameClient || !numChecked || isLoading}
                    alignItems="center"
                    sx={{ mr: 2 }}
                  >
                    Send Invoices
                  </MDButton>
                </span>
              </Tooltip>
              <MDButton
                onClick={() => {
                  setSelectedRows({});
                }}
                color="error"
                variant="contained"
                disabled={!numChecked || isLoading}
                alignItems="center"
              >
                Clear
              </MDButton>
            </Grid>
          }
        />
      </MDBox>
      {selectedInvoiceForPreview && (
        <InvoiceEditorPreviewModal
          open={!!selectedInvoiceForPreview}
          setOpen={() => setSelectedInvoiceForPreview(null)}
          detailRows={previewInvoiceData?.details ?? []}
          summaryRows={calculatedSummary ?? []}
          setInvoiceFile={setInvoiceFile}
          currentEvent={{ eventName: previewInvoiceData?.eventName }}
          currentJob={{ title: previewInvoiceData?.jobName }}
          invoiceNumber={
            previewInvoiceData?.invoiceNumber
              ? previewInvoiceData?.invoiceNumber.toString().padStart(8, "0")
              : null
          }
          startDate={previewInvoiceData?.startDate}
          from={{
            name: previewInvoiceData?.remittanceInformation?.name,
            address: previewInvoiceData?.remittanceInformation?.address,
            city: previewInvoiceData?.remittanceInformation?.city,
            state: previewInvoiceData?.remittanceInformation?.state,
            zip: previewInvoiceData?.remittanceInformation?.zip,
            notes: previewInvoiceData?.remittanceInformation?.notes,
            attn: previewInvoiceData?.remittanceInformation?.attn,
          }}
          to={{
            name: previewInvoiceData?.customerInformation?.name,
            address: previewInvoiceData?.customerInformation?.address,
            city: previewInvoiceData?.customerInformation?.city,
            state: previewInvoiceData?.customerInformation?.state,
            zip: previewInvoiceData?.customerInformation?.zip,
            attn: previewInvoiceData?.customerInformation?.attn,
          }}
          invoiceInformation={{
            date: previewInvoiceData?.invoiceInformation?.date,
            dueDate: previewInvoiceData?.invoiceInformation?.dueDate,
            purchaseOrder: previewInvoiceData?.invoiceInformation?.purchaseOrder,
          }}
          isShiftJob={!!previewInvoiceData?.jobSlug}
          isLoading={isLoadingPreviewInvoiceData}
          burdenRate={{}}
          isVoid={previewInvoiceData?.status === "Void"}
          invoiceId={previewInvoiceData?._id}
        />
      )}
      {!!selectedInvoiceForPreview && !!previewInvoiceVenueCustomer && !!invoiceFile && (
        <SendInvoiceMessageModal
          open={!!invoiceFile}
          setOpen={() => setInvoiceFile(null)}
          parent="Venue"
          messageContext="Venue"
          attachmentContext="Venue"
          venueCustomer={previewInvoiceVenueCustomer}
          invoiceFile={invoiceFile}
          // venueId={venueId}
          // companyId={companyId}
        />
      )}
      {!!selectedInvoiceIdsForSend?.length && (
        <SendMultipleInvoicesModal
          open={!!selectedInvoiceIdsForSend?.length}
          setOpen={() => setSelectedInvoiceIdsForSend(null)}
          selectedIds={selectedInvoiceIdsForSend}
          setInvoiceFiles={setInvoiceFiles}
        />
      )}
      {!!selectedInvoiceIdsForSend?.length &&
        !!sendMultipleInvoicesVenueCustomer &&
        !!invoiceFiles && (
          <SendInvoiceMessageModal
            open={!!invoiceFiles}
            setOpen={() => setInvoiceFiles(null)}
            parent="Venue"
            messageContext="Venue"
            attachmentContext="Venue"
            venueCustomer={sendMultipleInvoicesVenueCustomer}
            invoiceFiles={invoiceFiles}
            // venueId={venueId}
            // companyId={companyId}
          />
        )}
      {!!selectedInvoiceForDeletion && (
        <ConfirmationModal
          isOpen={!!selectedInvoiceForDeletion}
          onClose={() => {
            setSelectedInvoiceForDeletion(null);
          }}
          title="Delete Invoice"
          body="Are you sure you want to delete the invoice?"
          onConfirm={(e) => {
            e.stopPropagation();
            handleDelete(selectedInvoiceForDeletion);
            setSelectedInvoiceForDeletion(false);
          }}
        />
      )}
      {!!selectedInvoiceForVoiding && (
        <ConfirmationModal
          isOpen={!!selectedInvoiceForVoiding}
          onClose={() => {
            setSelectedInvoiceForVoiding(null);
          }}
          title="Void Invoice"
          body="Are you sure you want to void the invoice?"
          onConfirm={(e) => {
            e.stopPropagation();
            handleVoid(selectedInvoiceForVoiding);
            setSelectedInvoiceForVoiding(false);
          }}
        />
      )}
      {pageLimitConfirmationModal}
    </Card>
  );
};
InvoicesTable.defaultProps = {
  title: "Invoices",
};

// Typechecking props
InvoicesTable.propTypes = {
  title: PropTypes.string,
};

export default InvoicesTable;
